import React, { useState, useContext, useRef, useEffect } from 'react';
import { Tag, Phone, MessageCircle, Clock, ChevronDown } from 'lucide-react';
import { VendorContext } from '../context/VendorContext';
import './VendorDetails.css';

const VendorDetails = ({ 
  vendor = {}
}) => {
  const { vendorStatus } = useContext(VendorContext);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const {
    logoUrl = '/placeholder-logo.png',
    dispensaryName = '',
    minOrder = 0,
    acceptedPayments = [],
    dailyPromo = null,
    businessHours = {},
    contactNumbers = {},
  } = vendor || {};

  // Get the primary contact number
  const contactNumber = contactNumbers?.primary?.formatted || '';

  const formatTime = (time) => {
    if (!time) return 'Closed';
    
    if (!time.includes(':')) {
      const hour = parseInt(time, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}${ampm}`;
    }

    const [hour, minute] = time.split(':').map(Number);
    const formattedHour = hour % 12 || 12;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${formattedHour}:${minute.toString().padStart(2, '0')}${ampm}`;
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!vendor) return null;

  const currentDay = vendorStatus?.currentDay || '';
  const isOpen = vendorStatus?.status === 'open';

  const StatusToggle = () => {
    if (!vendorStatus) return null;

    return (
      <div
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className={`vendor-status-toggle ${isOpen ? 'vendor-status-toggle-open' : 'vendor-status-toggle-closed'}`}
      >
        <span 
          className={`vendor-status-indicator ${isOpen ? 'vendor-status-indicator-open' : 'vendor-status-indicator-closed'}`} 
        />
        <span style={{ display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {isOpen
            ? `Open until ${formatTime(vendorStatus.until)}`
            : `Opens at ${formatTime(vendorStatus.until)}`}
        </span>
        <ChevronDown size={14} style={{ marginLeft: '4px' }} />
      </div>
    );
  };

  return (
    <div className="details-vendor-card">
      <div className="details-vendor-card-body">
        {/* Header Section */}
        <div className="vendor-header-row">
          <div className="vendor-info-col">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={logoUrl}
                alt={`${dispensaryName || 'Vendor'} logo`}
                className="vendor-logo"
                onError={(e) => {
                  e.target.src = '/placeholder-logo.png';
                }}
              />
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <h6 className="vendor-heading">{dispensaryName}</h6>
                {vendorStatus && businessHours && Object.keys(businessHours).length > 0 && (
                  <div className="vendor-status-container" ref={dropdownRef}>
                    <StatusToggle />
                    <div className={`vendor-dropdown ${dropdownOpen ? '' : 'vendor-dropdown-hidden'}`}>
                      {minOrder > 0 && (
                        <div className="vendor-min-order">
                          <span>${minOrder} minimum order</span>
                        </div>
                      )}

                      <div className="vendor-hours-header">
                        <Clock size={16} style={{ color: '#666' }} />
                        <span>Business Hours</span>
                      </div>

                      <div>
                        {Object.entries(businessHours).map(([day, hours]) => (
                          <div 
                            key={day} 
                            className={`vendor-day-row ${day === currentDay ? 'vendor-day-current' : ''}`}
                          >
                            <span className={`vendor-day-name ${day === currentDay ? 'vendor-day-name-current' : ''}`}>
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </span>
                            <span className="vendor-day-hours">
                              {formatTime(hours.open)} - {formatTime(hours.close)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="vendor-contact-col">
            {contactNumber && (
              <>
                <a
                  href={`tel:${contactNumber}`}
                  className="vendor-contact-button vendor-call-button"
                  aria-label="Call dispensary"
                >
                  <Phone size={16} />
                </a>
                
              </>
            )}
          </div>
        </div>

        {/* Footer Section */}
        <div className="vendor-footer-row">
          <div className="vendor-payments-col">
            {acceptedPayments.length > 0 && (
              <div className="vendor-payment-badges">
                {acceptedPayments.map((payment, index) => (
                  <div
                    key={index}
                    className="vendor-payment-badge"
                  >
                    {payment.method}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="vendor-promo-col">
            {dailyPromo?.title && (
              <button
                onClick={() => setShowPromoModal(true)}
                className="vendor-promo-button"
              >
                <Tag size={14} />
                <span className="vendor-promo-text">
                  {dailyPromo.title}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Promo Modal */}
      {showPromoModal && dailyPromo && (
        <>
          <div className="vendor-modal-backdrop" onClick={() => setShowPromoModal(false)}>
            <div className="vendor-modal" onClick={(e) => e.stopPropagation()}>
              <div className="vendor-modal-header">
                <h5 className="vendor-modal-title">
                  {dailyPromo.title}
                </h5>
                <button 
                  className="vendor-modal-close" 
                  onClick={() => setShowPromoModal(false)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              
              <div className="vendor-modal-body">
                <p className="vendor-modal-description">
                  {dailyPromo.description || 'No description provided.'}
                </p>
                
                {dailyPromo.promoUrl && (
                  <div className="vendor-modal-image-container">
                    <img
                      src={dailyPromo.promoUrl}
                      alt="Promo"
                      className="vendor-modal-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <p className="vendor-modal-disclaimer">
                      Present coupon in-person during payment to apply.
                    </p>
                  </div>
                )}

                {dailyPromo.applicableToSaleItems !== undefined && (
                  <p className={`vendor-modal-applicable ${
                    dailyPromo.applicableToSaleItems 
                      ? 'vendor-applicable-yes' 
                      : 'vendor-applicable-no'
                  }`}>
                    {dailyPromo.applicableToSaleItems
                      ? 'Applicable to Sale Items!'
                      : 'Not applicable to sale items.'}
                  </p>
                )}
              </div>

              <div className="vendor-modal-footer">
                <button
                  className="vendor-modal-close-btn"
                  onClick={() => setShowPromoModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VendorDetails;